import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { Trophy, TrendingUp, Users, ArrowRight, Sparkles, ChevronDown, ChevronUp, Brain, RefreshCw, FileText, Download, BarChart3, PieChart as PieChartIcon } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { toast } from 'sonner';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function ClassCard({ classItem, index, onNavigate, basePath }) {
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [loadingAi, setLoadingAi] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const { getAuthHeaders } = useAuth();

  const fetchAiAnalysis = async () => {
    if (aiAnalysis) {
      setIsOpen(!isOpen);
      return;
    }
    
    setLoadingAi(true);
    setIsOpen(true);
    try {
      const response = await axios.get(`${API}/admin/ai-analysis/class/${classItem.class_id}`, {
        headers: getAuthHeaders(),
      });
      setAiAnalysis(response.data.analysis);
    } catch (error) {
      console.error('Error fetching AI analysis:', error);
      setAiAnalysis('AI талдау қолжетімсіз');
    } finally {
      setLoadingAi(false);
    }
  };

  return (
    <Card
      key={classItem.class_id}
      className="paper-card hover:shadow-lg transition-all duration-300 slide-in"
      style={{ animationDelay: `${index * 0.1}s` }}
      data-testid={`class-card-${classItem.class_id}`}
    >
      <CardHeader 
        className="cursor-pointer"
        onClick={() => onNavigate(`${basePath}/class/${classItem.class_id}`)}
      >
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="text-2xl heading-font text-foreground">
              {classItem.class_name}
            </CardTitle>
            <p className="text-sm text-muted mt-1">
              {classItem.student_count} оқушы
            </p>
          </div>
          <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
            <Trophy className="w-6 h-6 text-primary" />
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div 
            className="cursor-pointer"
            onClick={() => onNavigate(`${basePath}/class/${classItem.class_id}`)}
          >
            <div className="flex items-baseline space-x-2">
              <span className={`text-4xl font-bold heading-font score-animation ${
                classItem.average_score >= 0 ? 'text-green-600' : 'text-red-600'
              }`}>
                {classItem.average_score >= 0 ? '+' : ''}{classItem.average_score.toFixed(1)}
              </span>
              <span className="text-muted">ұпай</span>
            </div>
            <p className="text-xs text-muted mt-1">Орташа ұпай</p>
          </div>

          {/* AI Analysis Section */}
          <Collapsible open={isOpen} onOpenChange={setIsOpen}>
            <CollapsibleTrigger asChild>
              <Button 
                variant="outline" 
                size="sm" 
                className="w-full justify-between"
                onClick={(e) => {
                  e.stopPropagation();
                  fetchAiAnalysis();
                }}
              >
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-purple-500" />
                  <span>AI талдау</span>
                </div>
                {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="mt-3 p-3 bg-purple-50 rounded-lg border border-purple-200">
                {loadingAi ? (
                  <div className="flex items-center justify-center py-4">
                    <RefreshCw className="w-5 h-5 animate-spin text-purple-500 mr-2" />
                    <span className="text-sm text-purple-600">AI талдау жасалуда...</span>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-purple-700 font-medium">
                      <Brain className="w-4 h-4" />
                      <span className="text-sm">Тәрбиелік талдау</span>
                    </div>
                    <p className="text-sm text-gray-700 leading-relaxed">{aiAnalysis}</p>
                  </div>
                )}
              </div>
            </CollapsibleContent>
          </Collapsible>

          <div 
            className="flex items-center justify-between pt-4 border-t border-border/40 cursor-pointer"
            onClick={() => onNavigate(`${basePath}/class/${classItem.class_id}`)}
          >
            <span className="text-sm text-muted">Толық көру</span>
            <ArrowRight className="w-4 h-4 text-primary" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function AdminHome() {
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(true);
  const [topStudents, setTopStudents] = useState([]);
  const [activeTeachers, setActiveTeachers] = useState([]);
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const { subdomain } = useParams();
  
  const basePath = subdomain ? `/${subdomain}/admin` : '/admin';

  useEffect(() => {
    fetchClasses();
    fetchAnalytics();
    fetchTopStudents();
    fetchActiveTeachers();
  }, []);

  const fetchClasses = async () => {
    try {
      const response = await axios.get(`${API}/admin/classes`, {
        headers: getAuthHeaders(),
      });
      setClasses(response.data);
    } catch (error) {
      console.error('Error fetching classes:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const response = await axios.get(`${API}/admin/analytics`, {
        headers: getAuthHeaders(),
      });
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoadingAnalytics(false);
    }
  };

  const fetchTopStudents = async () => {
    try {
      // Use authenticated admin endpoint for proper school data isolation
      const response = await axios.get(`${API}/admin/top-students`, {
        headers: getAuthHeaders()
      });
      setTopStudents(response.data);
    } catch (error) {
      console.error('Error fetching top students:', error);
    }
  };

  const fetchActiveTeachers = async () => {
    try {
      const response = await axios.get(`${API}/admin/active-teachers`, {
        headers: getAuthHeaders()
      });
      setActiveTeachers(response.data);
    } catch (error) {
      console.error('Error fetching active teachers:', error);
    }
  };

  const downloadClassesPdf = async () => {
    setDownloadingPdf(true);
    try {
      const response = await axios.get(`${API}/admin/reports/classes-pdf`, {
        headers: getAuthHeaders(),
        responseType: 'blob',
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `synyp_esep_${new Date().toISOString().slice(0, 7)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF есебі жүктелді');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('PDF жүктеу кезінде қате орын алды');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const downloadExcel = async (type) => {
    try {
      toast.info('Excel файлы дайындалуда...');
      
      const endpoints = {
        'classes': '/admin/export/classes',
        'students': '/admin/export/students',
        'monthly-report': '/admin/export/monthly-report'
      };
      
      const filenames = {
        'classes': `synyptar_${new Date().toISOString().slice(0, 7)}.xlsx`,
        'students': `oqushylar_${new Date().toISOString().slice(0, 7)}.xlsx`,
        'monthly-report': `aylyq_esep_${new Date().toISOString().slice(0, 7)}.xlsx`
      };
      
      const response = await axios.get(`${API}${endpoints[type]}`, {
        headers: getAuthHeaders(),
        responseType: 'blob',
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filenames[type]);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Excel файлы жүктелді');
    } catch (error) {
      console.error('Error downloading Excel:', error);
      toast.error('Excel жүктеу кезінде қате орын алды');
    }
  };

  if (loading) {
    return (
      <div className="min-h-[400px] flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-4 border-teal-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-slate-600 font-medium">Жүктелуде...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8 fade-in" data-testid="admin-home">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-3xl font-bold heading-font tracking-tight mb-2 text-slate-900">
            Сыныптардың орташа ұпайлары
          </h2>
          <p className="text-slate-500">
            Ағымдағы айдың жалпы көрінісі
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button 
            variant="outline" 
            onClick={downloadClassesPdf}
            disabled={downloadingPdf}
            className="bg-white border-slate-200 text-slate-700 hover:bg-slate-50"
          >
            {downloadingPdf ? (
              <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Download className="mr-2 h-4 w-4" />
            )}
            PDF есебі
          </Button>
          <Button 
            variant="outline" 
            onClick={() => downloadExcel('classes')}
            className="bg-emerald-50 hover:bg-emerald-100 border-emerald-200 text-emerald-700"
          >
            <FileText className="mr-2 h-4 w-4 text-emerald-600" />
            Сыныптар Excel
          </Button>
          <Button 
            variant="outline" 
            onClick={() => downloadExcel('students')}
            className="bg-blue-50 hover:bg-blue-100 border-blue-200 text-blue-700"
          >
            <FileText className="mr-2 h-4 w-4 text-blue-600" />
            Оқушылар Excel
          </Button>
          <Button 
            variant="outline" 
            onClick={() => downloadExcel('monthly-report')}
            className="bg-violet-50 hover:bg-violet-100 border-violet-200 text-violet-700"
          >
            <BarChart3 className="mr-2 h-4 w-4 text-violet-600" />
            Айлық есеп
          </Button>
        </div>
      </div>

      {/* Summary Stats Cards */}
      {!loadingAnalytics && analytics && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {/* Total Students */}
          <Card className="relative overflow-hidden bg-gradient-to-br from-blue-500 to-blue-600 text-white border-0 hover:shadow-xl hover:scale-[1.02] transition-all duration-300">
            <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16"></div>
            <CardContent className="p-6 relative">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-blue-100">Жалпы оқушылар</p>
                  <h3 className="text-4xl font-bold mt-2">
                    {classes.reduce((sum, cls) => sum + (cls.student_count || 0), 0)}
                  </h3>
                  <p className="text-xs text-blue-100 mt-2 flex items-center">
                    <TrendingUp className="w-3 h-3 mr-1" />
                    {classes.length} сынып
                  </p>
                </div>
                <div className="h-14 w-14 bg-white/20 rounded-2xl flex items-center justify-center">
                  <Users className="h-7 w-7 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Active Teachers */}
          <Card className="relative overflow-hidden bg-gradient-to-br from-emerald-500 to-emerald-600 text-white border-0 hover:shadow-xl hover:scale-[1.02] transition-all duration-300">
            <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16"></div>
            <CardContent className="p-6 relative">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-emerald-100">Белсенді мұғалімдер</p>
                  <h3 className="text-4xl font-bold mt-2">
                    {analytics.top_behaviors.length > 0 ? '12' : '0'}
                  </h3>
                  <p className="text-xs text-emerald-100 mt-2 flex items-center">
                    <TrendingUp className="w-3 h-3 mr-1" />
                    Осы айда
                  </p>
                </div>
                <div className="h-14 w-14 bg-white/20 rounded-2xl flex items-center justify-center">
                  <Users className="h-7 w-7 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Total Points This Month */}
          <Card className="relative overflow-hidden bg-gradient-to-br from-violet-500 to-violet-600 text-white border-0 hover:shadow-xl hover:scale-[1.02] transition-all duration-300">
            <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16"></div>
            <CardContent className="p-6 relative">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-violet-100">Осы айдағы ұпай</p>
                  <h3 className="text-4xl font-bold mt-2">
                    {analytics.merit_demerit_dist.merit.count + analytics.merit_demerit_dist.demerit.count}
                  </h3>
                  <p className="text-xs text-violet-100 mt-2 flex items-center">
                    <Sparkles className="w-3 h-3 mr-1" />
                    {analytics.merit_demerit_dist.merit.count} марапат
                  </p>
                </div>
                <div className="h-14 w-14 bg-white/20 rounded-2xl flex items-center justify-center">
                  <Trophy className="h-7 w-7 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Average Class Score */}
          <Card className="relative overflow-hidden bg-gradient-to-br from-amber-500 to-orange-500 text-white border-0 hover:shadow-xl hover:scale-[1.02] transition-all duration-300">
            <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16"></div>
            <CardContent className="p-6 relative">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-amber-100">Орташа көрсеткіш</p>
                  <h3 className="text-4xl font-bold mt-2">
                    {classes.length > 0 
                      ? (classes.reduce((sum, cls) => sum + (cls.average_score || 0), 0) / classes.length).toFixed(1)
                      : '0.0'
                    }
                  </h3>
                  <p className="text-xs text-amber-100 mt-2 flex items-center">
                    <TrendingUp className="w-3 h-3 mr-1" />
                    Барлық сыныптар
                  </p>
                </div>
                <div className="h-14 w-14 bg-white/20 rounded-2xl flex items-center justify-center">
                  <TrendingUp className="h-7 w-7 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Top Students & Teachers Section */}
      {!loadingAnalytics && analytics && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Top 10 Students */}
          <Card className="paper-card border-0 shadow-lg">
            <CardHeader className="pb-3 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 rounded-t-lg">
              <CardTitle className="flex items-center gap-2 text-foreground">
                <div className="p-2 bg-amber-500 rounded-lg">
                  <Trophy className="w-5 h-5 text-white" />
                </div>
                <div>
                  <span className="text-lg text-foreground">Үздік 10 оқушы</span>
                  <p className="text-xs text-muted font-normal mt-0.5">Осы айдағы ең жоғары ұпайлы оқушылар</p>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4">
              <div className="space-y-2">
                {topStudents.length > 0 ? topStudents.slice(0, 10).map((student, index) => {
                  const medals = ['🥇', '🥈', '🥉'];
                  const bgColors = ['bg-gradient-to-r from-amber-100 to-yellow-50', 'bg-gradient-to-r from-slate-100 to-gray-50', 'bg-gradient-to-r from-orange-100 to-amber-50'];
                  const bgColor = index < 3 ? bgColors[index] : 'bg-slate-50';
                  return (
                    <div 
                      key={student.student_id} 
                      className={`flex items-center gap-3 p-3 rounded-xl ${bgColor} transition-all duration-200 cursor-pointer hover:shadow-md hover:scale-[1.01]`}
                      onClick={() => navigate(`${basePath}/student/${student.student_id}`)}
                    >
                      <div className="h-10 w-10 flex items-center justify-center">
                        {index < 3 ? (
                          <span className="text-2xl">{medals[index]}</span>
                        ) : (
                          <div className="h-8 w-8 bg-slate-300 rounded-full flex items-center justify-center text-slate-700 font-bold text-sm">
                            {index + 1}
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-sm truncate text-slate-800">{student.student_name}</p>
                        <p className="text-xs text-slate-600">{student.class_name}</p>
                      </div>
                      <div className="text-right">
                        <p className={`font-bold text-lg ${student.total_score >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {student.total_score >= 0 ? '+' : ''}{student.total_score}
                        </p>
                      </div>
                    </div>
                  );
                }) : (
                  <p className="text-sm text-muted text-center py-4">Әзірге оқушы жоқ</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Top 5 Teachers */}
          <Card className="paper-card border-0 shadow-lg">
            <CardHeader className="pb-3 bg-gradient-to-r from-emerald-50 to-green-50 dark:from-emerald-900/20 dark:to-green-900/20 rounded-t-lg">
              <CardTitle className="flex items-center gap-2">
                <div className="p-2 bg-emerald-500 rounded-lg">
                  <Users className="w-5 h-5 text-white" />
                </div>
                <div>
                  <span className="text-lg">Белсенді мұғалімдер</span>
                  <p className="text-xs text-muted font-normal mt-0.5">Осы айдағы ең белсенді 5 мұғалім</p>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4">
              <div className="space-y-2">
                {activeTeachers.length > 0 ? activeTeachers.slice(0, 5).map((teacher, index) => {
                  const maxPoints = activeTeachers[0]?.record_count || 100;
                  const colors = ['from-emerald-500 to-green-500', 'from-blue-500 to-cyan-500', 'from-violet-500 to-purple-500', 'from-rose-500 to-pink-500', 'from-amber-500 to-orange-500'];
                  return (
                    <div key={teacher.teacher_id || index} className="flex items-center gap-3 p-3 rounded-xl hover:bg-accent/50 transition-all duration-200 hover:shadow-md hover:scale-[1.01]">
                      <div className={`h-11 w-11 bg-gradient-to-br ${colors[index]} rounded-xl flex items-center justify-center text-white font-bold text-lg shadow-lg`}>
                        {teacher.teacher_name?.charAt(0) || '?'}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-sm text-foreground">{teacher.teacher_name}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <div className="flex-1 h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                            <div 
                              className={`h-full bg-gradient-to-r ${colors[index]} rounded-full`}
                              style={{ width: `${(teacher.record_count / maxPoints) * 100}%` }}
                            ></div>
                          </div>
                          <span className="text-xs font-medium text-muted">{teacher.record_count} жазба</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="text-lg">
                          {index === 0 ? '🏆' : index === 1 ? '⭐' : index === 2 ? '✨' : ''}
                        </span>
                      </div>
                    </div>
                  );
                }) : (
                  <p className="text-center text-muted py-4">Белсенді мұғалім жоқ</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Analytics Charts */}
      {!loadingAnalytics && analytics && (
        <div className="space-y-6 mb-8">
          <div>
            <h3 className="text-2xl font-bold mb-2 text-foreground">Статистикалық талдау</h3>
            <p className="text-sm text-muted">Соңғы 30 күннің деректері негізінде</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Daily Trend Chart */}
            <Card className="paper-card border-l-4 border-l-blue-500">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <TrendingUp className="w-5 h-5 text-blue-600" />
                  Күнделікті үрдіс
                </CardTitle>
                <p className="text-xs text-muted mt-1">Соңғы 30 күндегі марапат және ескерту динамикасы</p>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={280}>
                  <LineChart data={analytics.daily_trend}>
                    <defs>
                      <linearGradient id="meritGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                      </linearGradient>
                      <linearGradient id="demeritGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#ef4444" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis 
                      dataKey="date" 
                      tickFormatter={(date) => format(new Date(date), 'dd/MM')}
                      tick={{ fontSize: 12 }}
                      stroke="#9ca3af"
                    />
                    <YAxis tick={{ fontSize: 12 }} stroke="#9ca3af" />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'rgba(255, 255, 255, 0.95)', 
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                      }}
                    />
                    <Legend wrapperStyle={{ fontSize: '14px', paddingTop: '10px' }} />
                    <Line 
                      type="monotone" 
                      dataKey="merit" 
                      stroke="#10b981" 
                      strokeWidth={3}
                      dot={{ fill: '#10b981', r: 4 }}
                      activeDot={{ r: 6 }}
                      name="Марапат"
                    />
                    <Line 
                      type="monotone" 
                      dataKey="demerit" 
                      stroke="#ef4444" 
                      strokeWidth={3}
                      dot={{ fill: '#ef4444', r: 4 }}
                      activeDot={{ r: 6 }}
                      name="Ескерту"
                    />
                  </LineChart>
                </ResponsiveContainer>
                <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                  <p className="text-xs text-blue-800">
                    💡 <strong>Түсініктеме:</strong> Жасыл сызық оң мінез-құлықтарды (марапат), қызыл сызық теріс мінез-құлықтарды (ескерту) көрсетеді. Жоғары жасыл сызық - оң үрдіс.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Top Behaviors Chart */}
            <Card className="paper-card border-l-4 border-l-purple-500">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <BarChart3 className="w-5 h-5 text-purple-600" />
                  Жиі қолданылатын мінез-құлықтар
                </CardTitle>
                <p className="text-xs text-muted mt-1">Мұғалімдер ең көп қолданған 10 мінез-құлық түрі</p>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={analytics.top_behaviors}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis 
                      dataKey="name" 
                      angle={-35} 
                      textAnchor="end" 
                      height={100}
                      tick={{ fontSize: 11 }}
                      stroke="#9ca3af"
                    />
                    <YAxis tick={{ fontSize: 12 }} stroke="#9ca3af" />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'rgba(255, 255, 255, 0.95)', 
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px'
                      }}
                    />
                    <Bar 
                      dataKey="count" 
                      fill="#8b5cf6"
                      radius={[8, 8, 0, 0]}
                      name="Қолдану саны"
                    >
                      {analytics.top_behaviors.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.type === 'merit' ? '#10b981' : '#f59e0b'} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
                <div className="mt-3 p-3 bg-purple-50 rounded-lg">
                  <p className="text-xs text-purple-800">
                    💡 <strong>Түсініктеме:</strong> Жасыл - марапат мінез-құлықтары, сары - ескерту мінез-құлықтары. Биік бағандар жиі қолданылатын түрлерді көрсетеді.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Merit vs Demerit Pie Chart */}
            <Card className="paper-card border-l-4 border-l-green-500">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <PieChartIcon className="w-5 h-5 text-green-600" />
                  Марапат / Ескерту қатынасы
                </CardTitle>
                <p className="text-xs text-muted mt-1">Оң және теріс ұпайлардың процентті бөлінісі</p>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie
                      data={[
                        { name: 'Марапат', value: analytics.merit_demerit_dist.merit.count },
                        { name: 'Ескерту', value: analytics.merit_demerit_dist.demerit.count }
                      ]}
                      cx="50%"
                      cy="50%"
                      labelLine={true}
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
                      outerRadius={90}
                      dataKey="value"
                    >
                      <Cell fill="#10b981" />
                      <Cell fill="#ef4444" />
                    </Pie>
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'rgba(255, 255, 255, 0.95)', 
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px'
                      }}
                    />
                    <Legend 
                      verticalAlign="bottom" 
                      height={36}
                      wrapperStyle={{ fontSize: '14px' }}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <div className="mt-3 p-3 bg-green-50 rounded-lg">
                  <p className="text-xs text-green-800">
                    💡 <strong>Түсініктеме:</strong> Жасыл - марапат (оң мінез-құлық), қызыл - ескерту (теріс мінез-құлық). Жасыл үлесі жоғары болса - оң ахуал.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Class Performance Comparison */}
            <Card className="paper-card border-l-4 border-l-amber-500">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Trophy className="w-5 h-5 text-amber-600" />
                  Сыныптардың орташа көрсеткіштері
                </CardTitle>
                <p className="text-xs text-muted mt-1">Әр сыныптың оқушы басына орташа ұпайы</p>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={analytics.class_performance} layout="horizontal">
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis 
                      dataKey="class_name"
                      tick={{ fontSize: 12 }}
                      stroke="#9ca3af"
                    />
                    <YAxis tick={{ fontSize: 12 }} stroke="#9ca3af" />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'rgba(255, 255, 255, 0.95)', 
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px'
                      }}
                      formatter={(value) => [`${value} ұпай`, 'Орташа']}
                    />
                    <Bar 
                      dataKey="avg_score" 
                      radius={[8, 8, 0, 0]}
                      name="Орташа ұпай"
                    >
                      {analytics.class_performance.map((entry, index) => (
                        <Cell 
                          key={`cell-${index}`} 
                          fill={entry.avg_score >= 0 ? '#f59e0b' : '#ef4444'} 
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
                <div className="mt-3 p-3 bg-amber-50 rounded-lg">
                  <p className="text-xs text-amber-800">
                    💡 <strong>Түсініктеме:</strong> Сары - оң көрсеткіш, қызыл - теріс көрсеткіш. Биік бағандар ең жақсы нәтиже көрсеткен сыныптарды білдіреді.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Classes Grid */}
      {classes.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted">Сыныптар табылмады</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {classes.map((classItem, index) => (
            <ClassCard 
              key={classItem.class_id} 
              classItem={classItem} 
              index={index} 
              onNavigate={navigate}
              basePath={basePath}
            />
          ))}
        </div>
      )}
    </div>
  );
}
